/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { I18nContext } from '@bingads-webui-react/i18n-context';
import _ from 'underscore';
import classnames from 'classnames';
import { Link, Label, Stack } from '@bingads-webui-react/fabric-control';
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState, useEffect } from 'react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AdSchedule, AudienceAds, BaseExclusion, UrlOptions, usePatch, isAudienceAdsValid,
  getUrlOptionsDetails, getExclusionsDetails, globalErrorMsg, getAudienceAdsDetails } from '@bingads-webui-campaign/hotel-common-upsert';
import { AdDistribution } from '@bingads-webui-campaign/ad-distribution';
import { checkShouldShowError, getAdScheduleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { useCurrentCampaign, useCurrentAdGroup } from '@bingads-webui-campaign-react/use-current-scope-entity';
import { Collapse, ToggleTitle, TextTitle } from '@bingads-webui-campaign/wizard-jumbo-card';
import { getUnSupportedBidAdjustmentFeatures } from '../util';

const getStyles = () =>
  ({
    advancedSettingsSection: {
      marginTop: '19px',
      '& .displayNone': {
        display: 'none',
      },
      '& .expandedImage:before, .expanderImage:before': {
        marginRight: '12px',
      },
      '& .urloptions-wraper': {
        '& h5.control-label': {
          display: 'none',
        },
      },
    },
    advancedSettingsContainer: {
    },
  });

function getAdDistributionOption(adgroup) {
  return {
    AdDistribution: adgroup.AdDistribution,
    Medium: adgroup.Medium,
    Languages: ['All'],
    campaignType: 'hotel',
  };
}

function getAdScheduleOption(campaign, adgroup) {
  const model = {
    dayTimeRanges: adgroup.dayTimeRanges,
    timeZone: adgroup.TimeZone,
    adScheduleUseSearcherTimeZone: adgroup.AdScheduleUseSearcherTimeZone,
    startDate: adgroup.StartDate,
    endDate: adgroup.EndDate,
    adGroupStatus: adgroup.Status,
  };
  return {
    model,
    level: 'adgroup',
    campaignDayTimeRanges: campaign && campaign.dayTimeRanges,
    showDateSettings: true,
    hideAdScheduleBidAdjustments: true,
    campaignAdScheduleUseSearcherTimeZone: campaign && campaign.AdScheduleUseSearcherTimeZone,
  };
}

export const AdvancedSettings = (props) => {
  const {
    scenario,
    errMsg,
    patchErrMsg,
    getAdScheduleView,
    setAdScheduleView,
    setAdDistributionView,
    getAdDistributionView,
    setUrlOptionsView,
    setSiteExclusionView,
    patchAdgroup,
    adgroup,
    campaign,
    getUrlOptionsValues,
    getExclusionsValues,
    advancedSettinsApiErrors,
    isDataFetched,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const currentCampaign = useCurrentCampaign();
  const currentAdgroup = useCurrentAdGroup();
  const campaignId = currentCampaign ? currentCampaign.Id : null;
  const adgroupId = currentAdgroup ? currentAdgroup.Id : null;

  const campaignBidStrategyType = campaign && campaign.bidStrategy && campaign.bidStrategy.option;
  // We still need to support start & end date in the adSchedule view
  const unSupportedFeatures = getUnSupportedBidAdjustmentFeatures(campaignBidStrategyType).filter(x => x !== 'adSchedule');
  const allPanelItems = _.reject(['adSchedule', 'adDistribution', 'audienceAds', 'urlOptions', 'baseExclusion'], x => unSupportedFeatures.includes(x));
  const [activeKeys, setActiveKeys] = useState(_.reject(['adSchedule', 'audienceAds', 'adDistribution'], x => unSupportedFeatures.includes(x)));

  useEffect(() => {
    const hasApiErrorKeys = Object.keys(advancedSettinsApiErrors);
    const needToExpandKeys = [];
    allPanelItems.forEach((key) => {
      if (hasApiErrorKeys.includes(key) && advancedSettinsApiErrors[key] && advancedSettinsApiErrors[key].length) {
        patchErrMsg(key, globalErrorMsg);
        needToExpandKeys.push(key);
      }
    });
    setActiveKeys(activeKeys.concat(needToExpandKeys));
  }, [advancedSettinsApiErrors]);

  const onCollapseAll = () => {
    if (activeKeys.length) {
      setActiveKeys([]);
    } else {
      setActiveKeys(allPanelItems);
    }
  };

  const setAdSchedule = usePatch(patchAdgroup, 'adSchedule');
  const setAudienceAds = usePatch(patchAdgroup, 'audienceAds');

  const {
    adSchedule,
    audienceAds,
  } = adgroup;
  // Turn off --- you set nothing, you use default settings 0 0 0
  // Turn on --- you override 0 0 0
  const onChangeToggle = (fn, key, value) => {
    fn({ ...adgroup[key], useParentLevel: !value });
  };

  const adScheduleTitle = (<TextTitle
    titleKey={_TL_('Ad schedule')}
    topicKey="pop_BA_Targeting_DayTime"
    details={getAdScheduleDetails(i18n, getAdScheduleView())}
    isChecked={!adSchedule.useParentLevel}
    handleChangeToggle={(_e, value) => {
      onChangeToggle(setAdSchedule, 'adSchedule', value);
      const adScheduleView = getAdScheduleView();
      if (adScheduleView) {
        adScheduleView.koModel.useCampaignSettingsChecked(!value);
      }
    }}
  />);

  // After collecting the needed data, need to ensure the view is created once.
  const adScheduleOption = useMemo(
    () => (getAdScheduleOption(campaign, adgroup)),
    [campaign.dayTimeRanges, adgroup.dayTimeRanges, campaign.AdScheduleUseSearcherTimeZone, adgroup.adScheduleUseSearcherTimeZone]
  );


  const audienceAdsTitle =
    (<ToggleTitle
      titleKey={_TL_('Audience ads')}
      details={getAudienceAdsDetails({ rawData: audienceAds, i18n })}
      isChecked={!audienceAds.useParentLevel}
      handleChangeToggle={(_e, value) => { onChangeToggle(setAudienceAds, 'audienceAds', value); }}
    />);


  const adDistributionOption = useMemo(
    () => getAdDistributionOption(adgroup),
    [adgroup.AdDistribution, adgroup.Medium]
  );

  const setUrlOptionsActiveCallback = () => { patchAdgroup('urlOptions', getUrlOptionsValues()); };
  const setExclusionsActiveCallback = () => { patchAdgroup('exclusions', getExclusionsValues()); };

  return (
    <div className={classnames('advanced-settings-section', classes.advancedSettingsSection)}>
      <div className={classes.advancedSettingsContainer}>
        <Stack horizontal horizontalAlign="space-between" tokens={{ padding: '0 0 12px 0' }}>
          <Label>{i18n.getString(_TL_('ADVANCED SETTINGS'))}</Label>
          <Link className="jumbo-card-control-all-btn" onClick={onCollapseAll}>{activeKeys.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
        </Stack>
        <Collapse allPanelItems={allPanelItems} checkItems activeKeys={activeKeys} setActiveKeys={setActiveKeys}>
          {isDataFetched &&
            <Collapse.Panel key="adSchedule" title={adScheduleTitle} error={adSchedule.useParentLevel ? '' : errMsg.adSchedule}>
              <AdSchedule
                key="ad-schedule"
                setAdScheduleView={setAdScheduleView}
                options={adScheduleOption}
                getAdScheduleView={getAdScheduleView}
                getCreatedView={getAdScheduleView}
                managed
              />
            </Collapse.Panel>}
          <Collapse.Panel
            key="audienceAds"
            title={audienceAdsTitle}
            error={!isAudienceAdsValid(audienceAds) && checkShouldShowError(audienceAds) ? globalErrorMsg : ''}
          >
            <AudienceAds key="audience-ads" setAudienceAds={setAudienceAds} audienceAds={audienceAds} />
          </Collapse.Panel>
          <Collapse.Panel
            key="adDistribution"
            title={<TextTitle
              titleKey={_TL_('Ad distribution')}
              topicKey="pop_BA_NewCampaignCreate_AdDistribution"
              details={(() => {
              const view = getAdDistributionView();
              return view ? view.getTranslatedOption() : '';
            })()}
            />}
            error={errMsg.adDistribution}
          >
            <AdDistribution key="ad-distribution" setAdDistributionView={setAdDistributionView} options={adDistributionOption} />
          </Collapse.Panel>
          {
            scenario === 'settings' &&
            <Collapse.Panel key="urlOptions" title={<TextTitle titleKey={_TL_('URL options')} details={getUrlOptionsDetails(i18n, adgroup.urlOptions)} />} error={errMsg.urlOptions} setActiveCallback={setUrlOptionsActiveCallback}>
              <UrlOptions
                key="url-options"
                setUrlOptionsView={setUrlOptionsView}
                options={{
                level: 'adgroup', campaignId, adgroupId, model: adgroup.urlOptions,
              }}
              />
            </Collapse.Panel>
          }
          {
            scenario === 'settings' &&
            <Collapse.Panel key="baseExclusion" title={<TextTitle titleKey={_TL_('Website & IP address exclusions')} details={getExclusionsDetails(i18n, adgroup.exclusions)} />} error={errMsg.baseExclusion} setActiveCallback={setExclusionsActiveCallback}>
              <BaseExclusion
                patchData={patchAdgroup}
                key="base-exclusion"
                setSiteExclusionView={setSiteExclusionView}
                options={{
                  level: 'adgroup', campaignId, adgroupId, showSiteExclusion: true,
                }}
              />
            </Collapse.Panel>
          }
        </Collapse>
      </div>
    </div>
  );
};

AdvancedSettings.propTypes = {
  isDataFetched: PropTypes.string.isRequired,
  scenario: PropTypes.string.isRequired,
  errMsg: PropTypes.shape({}),
  patchErrMsg: PropTypes.func.isRequired,
  campaign: PropTypes.shape({}).isRequired,
  adgroup: PropTypes.shape({}).isRequired,
  patchAdgroup: PropTypes.func.isRequired,
  setAdScheduleView: PropTypes.func.isRequired,
  setAdDistributionView: PropTypes.func.isRequired,
  getAdDistributionView: PropTypes.func.isRequired,
  setUrlOptionsView: PropTypes.func.isRequired,
  setSiteExclusionView: PropTypes.func.isRequired,
  getAdScheduleView: PropTypes.func.isRequired,
  getUrlOptionsValues: PropTypes.func.isRequired,
  getExclusionsValues: PropTypes.func.isRequired,
  advancedSettinsApiErrors: PropTypes.shape({}),
};
AdvancedSettings.defaultProps = {
  errMsg: {},
  advancedSettinsApiErrors: {},
};

